/**
 * DviStageSession - Stage 0: Session management
 * 
 * Shows session status and allows creating/viewing session details.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Circle, Loader2, Plus, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DviStageSessionProps {
  session: {
    id: string;
    status: string;
    created_at: string;
  } | null;
  isLoading: boolean;
  onCreateSession: () => void;
  isCreating: boolean;
  canCreate: boolean;
}

const SESSION_STATUS_CONFIG: Record<string, { label: string; className: string; description: string }> = {
  draft: { 
    label: 'Draft', 
    className: 'bg-muted text-muted-foreground',
    description: 'Session created, not shared with customer yet'
  },
  shared: { 
    label: 'Shared', 
    className: 'bg-blue-500/10 text-blue-600',
    description: 'Link sent to customer, awaiting their review'
  },
  customer_viewed: { 
    label: 'Viewed', 
    className: 'bg-purple-500/10 text-purple-600',
    description: 'Customer has opened the inspection link'
  },
  customer_responded: { 
    label: 'Responded', 
    className: 'bg-green-500/10 text-green-600',
    description: 'Customer has approved/declined findings'
  },
  archived: { 
    label: 'Archived', 
    className: 'bg-muted text-muted-foreground',
    description: 'Session is closed and no longer accessible to customer'
  },
};

export function DviStageSession({
  session,
  isLoading,
  onCreateSession,
  isCreating,
  canCreate,
}: DviStageSessionProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-8 space-y-4">
        <Circle className="h-12 w-12 text-muted-foreground mx-auto" />
        <div>
          <p className="text-sm font-medium">No DVI Session</p>
          <p className="text-xs text-muted-foreground">Create a session to start the inspection workflow</p>
        </div>
        {canCreate && (
          <Button onClick={onCreateSession} disabled={isCreating} className="gap-2">
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            Create Session
          </Button>
        )}
      </div>
    );
  }

  const statusConfig = SESSION_STATUS_CONFIG[session.status] || SESSION_STATUS_CONFIG.draft;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Circle className="h-4 w-4" />
          Session Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Status:</span>
          <Badge variant="outline" className={cn('text-xs', statusConfig.className)}>
            {statusConfig.label}
          </Badge>
        </div>
        
        <div className="bg-muted/50 rounded p-2 flex items-start gap-2">
          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">{statusConfig.description}</p>
        </div>

        <div className="space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Session ID:</span>
            <span className="font-mono">{session.id.slice(0, 8)}...</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span>{new Date(session.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
